// A second runner: conversations as a small JSON node graph (suggestion: dialogue as a node graph with a strings table,
// flags and visit counts). Each node says its lines (string ids), then offers choices (each gated by a condition) or
// goes on to the next node, running story actions on the way. It shows the runner is a provider (decision 0055): a
// test swaps it in for ink, and a generated conversation (an AI writer's) could arrive in this shape.
import type { Condition, StoryAction } from "@rpg/story";
import type { DialogueBeat, DialogueRunner } from "./runner";

/** One node. */
export interface ScriptNode {
  lines?: readonly { id: string; speaker?: string }[];
  /** Run when the node is entered. */
  actions?: readonly StoryAction[];
  choices?: readonly { id: string; to: string | null; when?: Condition | string }[];
  /** Where to go after the lines when there are no choices (null or absent: the end). */
  next?: string | null;
}

/** A script: its nodes and where it starts. */
export interface NodeScript {
  start: string;
  nodes: Readonly<Record<string, ScriptNode>>;
}

interface Place {
  script: string;
  node: string;
  line: number;
  /** The choices offered, by index, with where each goes. */
  offered: (string | null)[] | null;
}

/** A runner over node scripts. `test` answers a choice's condition; `apply` runs a node's actions. */
export function createNodeRunner(o: { scripts: Readonly<Record<string, NodeScript>>; test(c: Condition | string): boolean; apply(actions: readonly StoryAction[]): void }): DialogueRunner {
  let place: Place | null = null;
  let visits: Record<string, number> = {};
  const node = (script: string, id: string): ScriptNode => {
    const n = o.scripts[script]?.nodes[id];
    if (!n) throw new Error(`dialogue: no node "${id}" in script "${script}"`);
    return n;
  };
  const enter = (script: string, id: string): void => {
    place = { script, node: id, line: 0, offered: null };
    const k = `${script}/${id}`;
    visits[k] = (visits[k] ?? 0) + 1;
    o.apply(node(script, id).actions ?? []);
  };
  return {
    scripts: () => Object.keys(o.scripts),
    start(script, knot) {
      const s = o.scripts[script];
      if (!s) throw new Error(`dialogue: no script "${script}"`);
      enter(script, knot ?? s.start);
    },
    next(): DialogueBeat {
      for (;;) {
        if (!place) return { kind: "end" };
        const n = node(place.script, place.node);
        const line = n.lines?.[place.line];
        if (line) {
          place.line++;
          return { kind: "line", id: line.id, speaker: line.speaker ?? null, tags: [`id:${line.id}`] };
        }
        const open = (n.choices ?? []).filter((c) => c.when === undefined || o.test(c.when));
        if (open.length) {
          place.offered = open.map((c) => c.to);
          return { kind: "choices", choices: open.map((c, index) => ({ index, id: c.id, tags: [`id:${c.id}`] })) };
        }
        if (n.next) enter(place.script, n.next);
        else place = null;
      }
    },
    choose(index) {
      const to = place?.offered?.[index];
      if (!place || to === undefined) throw new Error(`dialogue: no choice ${index} offered`);
      if (to === null) place = null;
      else enter(place.script, to);
    },
    snapshot: () => ({ place: place ? structuredClone(place) : null, visits: { ...visits } }),
    restore(data) {
      const d = data as { place: Place | null; visits: Record<string, number> };
      place = d.place ? structuredClone(d.place) : null;
      visits = { ...d.visits };
    },
  };
}
